import {
  useMedicalServiceMedicalDirectionsList,
  useMedicalServiceMedicalTravelInsurancesDirectionsPricesCreate
} from '@insurance/api/queries'
import { EntryTypeEnum, TravelCategoryEnum } from '@insurance/api/requests'
import { ConditionalText, DateTimeCell, Text } from '@shared/components'
import { useSessionContext } from '@shared/hooks'
import { getFormatCurrency } from '@shared/utils'
import { DescriptionsProps, Divider, Flex, Form } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useHealthInsuranceValues } from './HealthInsuranceContext'
import { useStyles } from './styles'
import { DetailsWrapper } from '../DetailsWrapper'

export const Details = () => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const { broker } = useSessionContext()
  const { isEditable, order } = useHealthInsuranceValues()
  const directions = Form.useWatch('directions', { preserve: true })
  const insurance = Form.useWatch('insurance', { preserve: true })
  const orderPrice = Form.useWatch('order_price', { preserve: true })
  const price = Form.useWatch('price', { preserve: true })
  const tripType = Form.useWatch('entry_type', { preserve: true })
  const validFrom = Form.useWatch('valid_from', { preserve: true })
  const validTo = Form.useWatch('valid_to', { preserve: true })
  const countDays = Form.useWatch('count_days', { preserve: true })
  const days = Form.useWatch('days', { preserve: true })
  const term = Form.useWatch('term', { preserve: true })
  const travelCategory = Form.useWatch('travel_category', { preserve: true })
  const sportCategory = Form.useWatch('sport_category', { preserve: true })
  const covid = Form.useWatch('covid', { preserve: true })
  const customAmount = Form.useWatch('custom_amount', { preserve: true })
  const credit = Form.useWatch('credit', { preserve: true })

  const isSingleTripType = tripType === EntryTypeEnum.SINGLE

  const { data: directionsData } = useMedicalServiceMedicalDirectionsList(
    { ids: directions?.join(',') },
    undefined,
    { enabled: !!directions?.length }
  )

  const { mutate: mutateInsurance, data: insuranceData } =
    useMedicalServiceMedicalTravelInsurancesDirectionsPricesCreate()

  useEffect(() => {
    const dataDirection = directions?.length ? directions : [1]

    mutateInsurance({ requestBody: { directions: dataDirection } })
  }, [directions, mutateInsurance])

  const foundInsurance = insuranceData?.find(
    ({ id }) => String(id) === String(insurance)
  )

  const items: DescriptionsProps['items'] = [
    ...(isEditable
      ? [
          {
            label: t('order_id'),
            children: order?.id
          }
        ]
      : []),
    {
      label: t('destination'),
      children: directions?.length
        ? directionsData?.results?.map(({ name }) => name).join(', ')
        : t('select_territory')
    },
    {
      label: t('insured_amount'),
      children: foundInsurance?.amount
    },
    ...(customAmount
      ? [
          {
            label: t('custom_amount'),
            children: customAmount
          }
        ]
      : []),
    { label: t('currency'), children: foundInsurance?.currency },
    {
      label: t('trip_type'),
      children: (
        <ConditionalText className={styles.text} condition={!!tripType}>
          {t(`${tripType}_entry`)}
        </ConditionalText>
      )
    },
    {
      label: t('beginning_insurance_policy'),
      children: (
        <DateTimeCell
          dateProps={{ className: styles.date }}
          date={validFrom}
          hiddenTime
        />
      )
    },
    ...(isSingleTripType
      ? [
          {
            label: t('ending_insurance_policy'),
            children: (
              <DateTimeCell
                dateProps={{ className: styles.date }}
                date={validTo}
                hiddenTime
              />
            )
          }
        ]
      : []),
    {
      label: t(isSingleTripType ? 'insured_days' : 'insured_period'),
      children: isSingleTripType ? (
        countDays
      ) : (
        <ConditionalText className={styles.text} condition={!!days}>
          {t('days_count', { count: days })}
        </ConditionalText>
      )
    },
    ...(isSingleTripType || !term
      ? []
      : [
          {
            label: t('insurance_validity'),
            children: t('months_count', { count: term })
          }
        ]),
    {
      label: t('purpose_of_the_trip'),
      children: (
        <ConditionalText className={styles.text} condition={!!travelCategory}>
          {t(travelCategory)}
        </ConditionalText>
      )
    },
    ...(sportCategory &&
    travelCategory === TravelCategoryEnum.SPORT_COMPETITIONS
      ? [
          {
            label: t('sport_category'),
            children: (
              <ConditionalText
                className={styles.text}
                condition={!!sportCategory}
              >
                {t(sportCategory)}
              </ConditionalText>
            )
          }
        ]
      : []),
    {
      label: t('covid_insurance'),
      children: covid ? t('includes_covid_risk') : t('not_includes_covid_risk')
    }
  ]

  const totalToBePaid = (price?.amount || 0) - (credit || 0)
  const totalToBePaidAfterModification =
    totalToBePaid - (orderPrice?.amount || 0)

  return (
    <DetailsWrapper items={items}>
      {price && !isEditable && (
        <>
          {!broker && !!credit && (
            <>
              <Flex align="center" justify="space-between">
                <Text strong size={18}>
                  {t('order_amount_paid')}
                </Text>
                <Text strong size={18} className="text-wrap-nowrap">
                  {getFormatCurrency(price)}
                </Text>
              </Flex>

              <Flex align="center" justify="space-between">
                <Text strong size={18}>
                  {t('amount_used_from_balance')}
                </Text>
                <Text strong size={18} className="text-wrap-nowrap">
                  {getFormatCurrency({ amount: credit })}
                </Text>
              </Flex>
            </>
          )}

          <Flex align="center" justify="space-between">
            <Text strong size={18}>
              {t('total_to_be_paid')}
            </Text>
            <Text strong size={18} className="text-wrap-nowrap">
              {getFormatCurrency({ amount: totalToBePaid })}
            </Text>
          </Flex>

          <Divider />
        </>
      )}

      {isEditable && (
        <>
          {orderPrice && (
            <Flex align="center" justify="space-between">
              <Text strong size={18}>
                {t('amount_paid')}
              </Text>
              <Text strong size={18} className="text-wrap-nowrap">
                {getFormatCurrency(orderPrice)}
              </Text>
            </Flex>
          )}
          {price && (
            <Flex align="center" justify="space-between">
              <Text strong size={18}>
                {t('new_amount')}
              </Text>
              <Text strong size={18} className="text-wrap-nowrap">
                {getFormatCurrency(price)}
              </Text>
            </Flex>
          )}
          {orderPrice && price && (
            <>
              {!broker && !!credit && (
                <Flex align="center" justify="space-between">
                  <Text strong size={18}>
                    {t('amount_used_from_balance')}
                  </Text>
                  <Text strong size={18} className="text-wrap-nowrap">
                    {getFormatCurrency({ amount: credit })}
                  </Text>
                </Flex>
              )}

              <Flex align="center" justify="space-between">
                <Text
                  strong
                  size={22}
                  type={
                    totalToBePaidAfterModification > 0 ? 'success' : 'danger'
                  }
                >
                  {t('total_to_be_paid')}
                </Text>
                <Text
                  strong
                  size={22}
                  type={
                    totalToBePaidAfterModification > 0 ? 'success' : 'danger'
                  }
                  className="text-wrap-nowrap"
                >
                  {getFormatCurrency({
                    amount: totalToBePaidAfterModification,
                    currency: price.currency
                  })}
                </Text>
              </Flex>
            </>
          )}

          <Divider />
        </>
      )}
    </DetailsWrapper>
  )
}
